import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import { 
  AccountBalanceWallet, 
  Star, 
  CheckCircle,
  Close 
} from '@mui/icons-material';

interface Plan {
  id: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  recurring: boolean;
  interval?: string;
  tokens_included?: number;
  product_id?: string;
}

interface TokenRechargeModalProps {
  open: boolean;
  onClose: () => void;
  currentTokens: number;
  requiredTokens: number;
}

const TokenRechargeModal: React.FC<TokenRechargeModalProps> = ({
  open,
  onClose,
  currentTokens,
  requiredTokens
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);

  // Fetch plans when modal opens
  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    setPlansLoading(true);
    try {
      const response = await fetch('/api/v1/subscriptions/plans');
      if (response.ok) {
        const plansData = await response.json();
        setPlans(plansData);
      } else {
        console.error('Failed to fetch plans');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setPlansLoading(false);
    }
  };

  const handleSubscribeToPlan = async (plan: Plan) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Redirect to subscription page with the selected plan
      window.location.href = `/subscriptions?plan=${plan.id}`;
    } catch (err: any) {
      setError('Failed to redirect to subscription page. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = () => {
    // Redirect to subscription page
    window.location.href = '/subscriptions';
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWallet sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Insufficient Tokens
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ 
            color: 'white', 
            minWidth: 'auto',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}>
            <Typography variant="body1">
              You need <strong>{requiredTokens} tokens</strong> to continue, but you only have <strong>{currentTokens} tokens</strong>.
            </Typography>
          </Alert>

          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            Choose one of the options below to get more tokens:
          </Typography>
        </Box>

        {/* Subscription Plans */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star sx={{ fontSize: 20 }} />
            Choose a Subscription Plan
          </Typography>
          
          {plansLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ color: 'white' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
              {plans.slice(0, 3).map((plan) => (
                <Button
                  key={plan.id}
                  variant="outlined"
                  onClick={() => handleSubscribeToPlan(plan)}
                  disabled={loading}
                  sx={{
                    p: 2,
                    flexDirection: 'column',
                    gap: 1,
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    position: 'relative',
                    textAlign: 'left',
                    alignItems: 'flex-start',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {plan.name === 'Test' && (
                    <Chip 
                      label="POPULAR" 
                      size="small" 
                      color="secondary"
                      sx={{ 
                        position: 'absolute', 
                        top: -8, 
                        right: -8,
                        fontSize: '0.7rem',
                        height: 20
                      }} 
                    />
                  )}
                  <Typography variant="h6" sx={{ fontWeight: 'bold', alignSelf: 'center' }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8, textAlign: 'center', fontSize: '0.8rem' }}>
                    ${(plan.amount / 100).toFixed(2)}/month
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem', textAlign: 'center' }}>
                    {plan.tokens_included?.toLocaleString()} tokens
                  </Typography>
                </Button>
              ))}
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} sx={{ color: 'white' }} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 2 }} />

        {/* All Plans Option */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ fontSize: 20 }} />
            View All Plans
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            See all available subscription plans with different token amounts:
          </Typography>

          <Box sx={{ mb: 2, pl: 2 }}>
            {plans.map((plan) => (
              <Typography key={plan.id} variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                • <strong>{plan.name}:</strong> {plan.tokens_included?.toLocaleString()} tokens/month for ${(plan.amount / 100).toFixed(2)}
              </Typography>
            ))}
          </Box>

          <Button
            variant="contained"
            onClick={handleSubscribe}
            sx={{
              backgroundColor: 'white',
              color: '#667eea',
              fontWeight: 'bold',
              px: 3,
              py: 1.5,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)'
              }
            }}
          >
            View All Plans
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          sx={{ 
            color: 'rgba(255,255,255,0.7)',
            '&:hover': { color: 'white' }
          }}
        >
          Maybe Later
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenRechargeModal;